
import { defineComponent, onMounted, ref } from "vue";
import { useStore } from "vuex";
import { ErrorMessage, Field, Form } from "vee-validate";
import { setCurrentPageBreadcrumbs } from "@/core/helpers/breadcrumb";
import Swal from "sweetalert2/dist/sweetalert2.js";
import * as Yup from "yup";
import Users from "@/views/admin/Users.vue";
import PaymentMethods from "@/components/customers/cards/overview/PaymentMethods.vue";
import Invoices from "@/components/customers/cards/overview/Invoices.vue";
import ApiService from "@/core/services/ApiService";
import { ImageInputComponent } from "@/assets/ts/components/_ImageInputComponent";

interface UserDetails {
  admintype: string;
  customer_id: string;
  companygroupid: string;
  adminfirstname: string;
  adminfullname: string;
  adminlastname: string;
  username: string;
  companyname: string;
  adminpic: string;
  adminid: string;
  adminstatus: string;
  admintitle: string;
}
interface ProfileDetails {
  avatar: string;
  name: string;
  surname: string;
  company: string;
  contactPhone: string;
  companySite: string;
  country: string;
  language: string;
  timezone: string;
  currency: string;
  communications: {
    email: boolean;
    phone: boolean;
  };
  allowMarketing: boolean;
}

export default defineComponent({
  name: "account-settings",
  components: {
    ErrorMessage,
    Field,
    Form,
    Users,
    PaymentMethods,
    Invoices,
  },
  setup() {
    const store = useStore();
    // eslint-disable-next-line
    const paymentProfiles: any = ref([]);

    const submitButton1 = ref<HTMLElement | null>(null);
    const submitButton2 = ref<HTMLElement | null>(null);
    const submitButton3 = ref<HTMLElement | null>(null);
    const submitButton4 = ref<HTMLElement | null>(null);
    const submitButton5 = ref<HTMLElement | null>(null);
    const updateEmailButton = ref<HTMLElement | null>(null);
    const updatePasswordButton = ref<HTMLElement | null>(null);
    const avatarInputRef = ref<HTMLElement | null>(null);

    const userData = ref<UserDetails>({
      admintype: "",
      customer_id: "",
      companygroupid: "",
      adminfirstname: "",
      adminfullname: "",
      adminlastname: "",
      username: "",
      companyname: "",
      adminpic: "",
      adminid: "",
      adminstatus: "",
      admintitle: "",
    });
    const userDetailFields = ref({
      adminid: "ID",
      adminfirstname: "First Name",
      adminlastname: "Last Name",
      adminfullname: "Full Name",
      username: "Email",
      companyname: "Company Name",
      customer_id: "Customer ID",
      companygroupid: "Company Group ID",
      adminstatus: "Status",
    });
    const emailFormDisplay = ref(false);
    const passwordFormDisplay = ref(false);
    const userName = ref("");
    const currentpassword = ref("");
    const confirmemailpassword = ref("");
    const newPassword = ref("");

    const profileDetailsValidator = Yup.object().shape({
      fname: Yup.string().required().label("First name"),
      lname: Yup.string().required().label("Last name"),
      company: Yup.string().required().label("Company"),
      phone: Yup.string().required().label("Contact phone"),
      website: Yup.string().label("Webside"),
      country: Yup.string().required().label("Country"),
      language: Yup.string().required().label("Language"),
      timezone: Yup.string().required().label("Timezone"),
      currency: Yup.string().required().label("Currency"),
    });

    const changeEmail = Yup.object().shape({
      username: Yup.string().required().email().label("Email"),
      confirmemailpassword: Yup.string().required().label("Password"),
    });

    const changePassword = Yup.object().shape({
      currentpassword: Yup.string().required().label("Current password"),
      newpassword: Yup.string().min(4).required().label("Password"),
      confirmpassword: Yup.string()
        .min(4)
        .required()
        .oneOf([Yup.ref("newpassword"), null], "Passwords must match")
        .label("Password Confirmation"),
    });

    const profileDetails = ref<ProfileDetails>({
      avatar: "",
      name: "Max",
      surname: "Smith",
      company: "Keenthemes",
      contactPhone: "044 3276 454 935",
      companySite: "keenthemes.com",
      country: "MY",
      language: "msa",
      timezone: "Kuala Lumpur",
      currency: "USD",
      communications: {
        email: false,
        phone: false,
      },
      allowMarketing: false,
    });

    const saveChanges1 = async () => {
      if (submitButton1.value) {
        // Activate indicator
        submitButton1.value.setAttribute("data-kt-indicator", "on");

        const imageInputComponent = ImageInputComponent?.getInstance(
          avatarInputRef.value as HTMLElement
        );
        var inputElement = imageInputComponent?.getInputElement();
        var isAvatarChanged = false;
        if (inputElement && inputElement.files[0]) {
          isAvatarChanged = await uploadAvatar(inputElement.files[0]);
        } else if (
          imageInputComponent?.isEmptySrc() &&
          profileDetails.value.avatar != ""
        ) {
          isAvatarChanged = true;
          removeAvatar();
        }

        try {
          // eslint-disable-next-line
          const payload: any = {};
          if (isAvatarChanged) {
            payload["adminpic"] = profileDetails.value.avatar;
          }
          const res = await ApiService.put(
            `users/accounts/${store.getters.currentUser.data.adminid}`,
            payload
          );

          if (res.data.success) {
            Swal.fire({
              text: "You have successfully Saved changes!",
              icon: "success",
              buttonsStyling: false,
              confirmButtonText: "Ok, got it!",
              customClass: {
                confirmButton: "btn fw-bold btn-light-primary",
              },
            });
          } else {
            Swal.fire({
              text: res.data.error.message,
              icon: "error",
              buttonsStyling: false,
              confirmButtonText: "Try again!",
              customClass: {
                confirmButton: "btn fw-bold btn-light-danger",
              },
            });
          }
        } catch (e) {
          Swal.fire({
            text: e.response.data.error
              ? e.response.data.error.message
              : "Something went wrong!",
            icon: "error",
            buttonsStyling: false,
            confirmButtonText: "Try again!",
            customClass: {
              confirmButton: "btn fw-bold btn-light-danger",
            },
          });
        }

        setTimeout(() => {
          submitButton1.value?.removeAttribute("data-kt-indicator");
        }, 2000);
      }
    };

    const saveChanges2 = () => {
      if (submitButton2.value) {
        // Activate indicator
        submitButton2.value.setAttribute("data-kt-indicator", "on");

        setTimeout(() => {
          submitButton2.value?.removeAttribute("data-kt-indicator");
        }, 2000);
      }
    };

    const saveChanges3 = () => {
      if (submitButton3.value) {
        // Activate indicator
        submitButton3.value.setAttribute("data-kt-indicator", "on");

        setTimeout(() => {
          submitButton3.value?.removeAttribute("data-kt-indicator");
        }, 2000);
      }
    };

    const saveChanges4 = () => {
      if (submitButton4.value) {
        // Activate indicator
        submitButton4.value.setAttribute("data-kt-indicator", "on");

        setTimeout(() => {
          submitButton4.value?.removeAttribute("data-kt-indicator");
        }, 2000);
      }
    };

    const deactivateAccount = () => {
      if (submitButton5.value) {
        // Activate indicator
        submitButton5.value.setAttribute("data-kt-indicator", "on");

        setTimeout(() => {
          submitButton5.value?.removeAttribute("data-kt-indicator");

          Swal.fire({
            text: "Email is successfully changed!",
            icon: "success",
            confirmButtonText: "Ok",
            buttonsStyling: false,
            customClass: {
              confirmButton: "btn btn-light-primary",
            },
          }).then(() => {
            emailFormDisplay.value = false;
          });
        }, 2000);
      }
    };

    const uploadAvatar = async (file) => {
      // eslint-disable-next-line
      const formData: any = new FormData();
      formData.append("upload", file);
      const res = await ApiService.post("imgupload", formData);
      if (res.data.fileurl) {
        profileDetails.value.avatar = res.data.fileurl;
        return true;
      }
      return false;
    };

    const removeAvatar = () => {
      profileDetails.value.avatar = "";
    };

    onMounted(() => {
      setCurrentPageBreadcrumbs("Settings", ["Account"]);
      setTimeout(async () => {
        await getUserData();
        await getPaymentProfiles();
        await getApis();
      }, 1000);
    });

    const getUserData = async () => {
      const { data } = await ApiService.get(
        `users/vw_users_login?adminid=${store.getters.currentUser.data.adminid}`
      );
      if (data.data[0]) {
        Object.keys(userData.value).forEach((key) => {
          if (data.data[0][key]) {
            userData.value[key] = data.data[0][key];
          }
        });
      }

      userName.value = store.getters.currentUser.data.username;
    };
    const getPaymentProfiles = async () => {
      const parsedBillingProfiles = JSON.parse(
        store.getters.currentUser.data.billing_profiles
      );
      const { data } = await ApiService.get(
        `billing/profile/${parsedBillingProfiles[0]}?fields=token_id`
      );
      paymentProfiles.value.push(...data.data);
    };
    const updateEmail = async () => {
      if (
        confirmemailpassword.value != store.getters.currentUser.data.password
      ) {
        Swal.fire({
          text: "Password is incorrect.",
          icon: "error",
          buttonsStyling: false,
          confirmButtonText: "Try again!",
          customClass: {
            confirmButton: "btn fw-bold btn-light-danger",
          },
        });
        return;
      }
      if (updateEmailButton.value) {
        // Activate indicator
        updateEmailButton.value.setAttribute("data-kt-indicator", "on");

        try {
          // eslint-disable-next-line
          const payload: any = {
            username: userName.value,
          };

          const res = await ApiService.put(
            `users/logins/${store.getters.currentUser.data.adminid}`,
            payload
          );

          if (res.data.success) {
            Swal.fire({
              text: "User name is successfully changed!",
              icon: "success",
              buttonsStyling: false,
              confirmButtonText: "Ok, got it!",
              customClass: {
                confirmButton: "btn fw-bold btn-light-primary",
              },
            }).then(() => {
              confirmemailpassword.value = "";
              emailFormDisplay.value = false;
            });
          } else {
            Swal.fire({
              text: res.data.error.message,
              icon: "error",
              buttonsStyling: false,
              confirmButtonText: "Try again!",
              customClass: {
                confirmButton: "btn fw-bold btn-light-danger",
              },
            });
          }
        } catch (e) {
          Swal.fire({
            text: e.response.data.error
              ? e.response.data.error.message
              : "Something went wrong!",
            icon: "error",
            buttonsStyling: false,
            confirmButtonText: "Try again!",
            customClass: {
              confirmButton: "btn fw-bold btn-light-danger",
            },
          });
        }

        updateEmailButton.value?.removeAttribute("data-kt-indicator");
      }
    };

    const cancelUpdateEmail = () => {
      userName.value = store.getters.currentUser.data.username;
      confirmemailpassword.value = "";
      emailFormDisplay.value = false;
    };

    const updatePassword = async () => {
      if (currentpassword.value != store.getters.currentUser.data.password) {
        Swal.fire({
          text: "Password is incorrect.",
          icon: "error",
          buttonsStyling: false,
          confirmButtonText: "Try again!",
          customClass: {
            confirmButton: "btn fw-bold btn-light-danger",
          },
        });
        return;
      }
      if (updatePasswordButton.value) {
        // Activate indicator
        updatePasswordButton.value.setAttribute("data-kt-indicator", "on");

        try {
          // eslint-disable-next-line
          const payload: any = {
            password: newPassword.value,
          };

          const res = await ApiService.put(
            `users/logins/${store.getters.currentUser.data.adminid}`,
            payload
          );

          if (res.data.success) {
            Swal.fire({
              text: "Password is successfully changed!",
              icon: "success",
              buttonsStyling: false,
              confirmButtonText: "Ok, got it!",
              customClass: {
                confirmButton: "btn fw-bold btn-light-primary",
              },
            }).then(() => {
              currentpassword.value = "";
              newPassword.value = "";
              passwordFormDisplay.value = false;
            });
          } else {
            Swal.fire({
              text: res.data.error.message,
              icon: "error",
              buttonsStyling: false,
              confirmButtonText: "Try again!",
              customClass: {
                confirmButton: "btn fw-bold btn-light-danger",
              },
            });
          }
        } catch (e) {
          Swal.fire({
            text: e.response.data.error
              ? e.response.data.error.message
              : "Something went wrong!",
            icon: "error",
            buttonsStyling: false,
            confirmButtonText: "Try again!",
            customClass: {
              confirmButton: "btn fw-bold btn-light-danger",
            },
          });
        }

        updatePasswordButton.value?.removeAttribute("data-kt-indicator");
      }
    };

    const cancelUpdatePassword = () => {
      currentpassword.value = "";
      newPassword.value = "";
      passwordFormDisplay.value = false;
    };

    const connectedApis = ref([]);

    const getApis = async () => {
      try {
        const { data } = await ApiService.get(
          "apis/apis.merchants?category=Cloud"
        );

        if (data.success) {
          connectedApis.value = data.data;
        } else {
          connectedApis.value = [];
        }
      } catch (e) {
        connectedApis.value = [];
      }
    };
    const onNewApiSubmit = () => {
      getApis();
    };

    return {
      userData,
      connectedApis,
      userDetailFields,
      submitButton1,
      submitButton2,
      submitButton3,
      submitButton4,
      submitButton5,
      saveChanges1,
      saveChanges2,
      saveChanges3,
      saveChanges4,
      deactivateAccount,
      profileDetails,
      emailFormDisplay,
      passwordFormDisplay,
      avatarInputRef,
      uploadAvatar,
      removeAvatar,
      profileDetailsValidator,
      changeEmail,
      changePassword,
      updateEmailButton,
      updatePasswordButton,
      updateEmail,
      updatePassword,
      cancelUpdateEmail,
      cancelUpdatePassword,
      newPassword,
      userName,
      currentpassword,
      confirmemailpassword,
      paymentProfiles,
      onNewApiSubmit,
    };
  },
});
